import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ANALYTICS_CONSENT_EVENT,
  getAnalyticsConsent,
  setAnalyticsConsent,
  type AnalyticsConsent,
} from "@/lib/analytics";
import { SIDE_EVENTS_HOST } from "@shared/sideEventsHub";

function useAnalyticsConsentChoice() {
  const [consent, setConsent] = useState<AnalyticsConsent | null>(() => getAnalyticsConsent());

  useEffect(() => {
    const handleConsentChange = (event: Event) => {
      setConsent((event as CustomEvent<AnalyticsConsent>).detail);
    };
    window.addEventListener(ANALYTICS_CONSENT_EVENT, handleConsentChange);
    return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, handleConsentChange);
  }, []);

  const choose = (nextConsent: AnalyticsConsent) => {
    setConsent(setAnalyticsConsent(nextConsent));
  };
  return { consent, choose };
}

export function AnalyticsConsentBanner() {
  const { consent, choose } = useAnalyticsConsentChoice();
  const cookiePolicyHref = typeof window !== "undefined" && window.location.hostname === SIDE_EVENTS_HOST
    ? "https://www.chiefintech.com/cookies"
    : "/cookies";

  if (consent !== null) return null;

  return (
    <aside
      aria-label="Analytics cookie choice"
      className="fixed inset-x-2 bottom-2 z-[100] mx-auto max-w-3xl rounded-xl border bg-background p-3 shadow-xl sm:inset-x-3 sm:bottom-3 sm:p-5"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <p className="text-xs leading-relaxed text-muted-foreground sm:text-sm">
          <span className="sm:hidden">
            Optional analytics help us improve founder resources. Review our{" "}
            <a href={cookiePolicyHref} className="font-medium text-foreground underline underline-offset-4">
              cookie policy
            </a>.
          </span>
          <span className="hidden sm:inline">
            We use optional Google Analytics, Microsoft Clarity session analytics, and on-site personalization measurement to understand which resources help founders. You can continue with only necessary storage. Read our{" "}
            <a href={cookiePolicyHref} className="font-medium text-foreground underline underline-offset-4">
              cookie policy
            </a>.
          </span>
        </p>
        <div className="grid shrink-0 grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <Button type="button" variant="outline" className="h-auto min-h-9 whitespace-normal px-3 py-2 text-xs sm:text-sm" onClick={() => choose("denied")}>
            Only necessary
          </Button>
          <Button type="button" className="h-auto min-h-9 whitespace-normal px-3 py-2 text-xs sm:text-sm" onClick={() => choose("granted")}>
            Allow analytics
          </Button>
        </div>
      </div>
    </aside>
  );
}

export function AnalyticsConsentSettings() {
  const { consent, choose } = useAnalyticsConsentChoice();

  return (
    <div className="space-y-4" data-testid="analytics-consent-settings">
      <p className="text-sm text-muted-foreground">
        Current choice: {consent === "granted" ? "Analytics allowed" : "Only necessary storage"}.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant={consent === "denied" ? "default" : "outline"}
          aria-pressed={consent === "denied"}
          onClick={() => choose("denied")}
        >
          Only necessary
        </Button>
        <Button
          type="button"
          variant={consent === "granted" ? "default" : "outline"}
          aria-pressed={consent === "granted"}
          onClick={() => choose("granted")}
        >
          Allow analytics
        </Button>
      </div>
    </div>
  );
}
